//src/pages/gro/SurveyResultsPage.tsx
import { useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Download, RefreshCw, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { SurveyCutsTable } from "@/components/gro/SurveyCutsTable";
import { SurveyResultsSummary } from "@/components/gro/SurveyResultsSummary";
import { SurveyDimensionsSection } from "@/components/gro/SurveyDimensionsSection";
import { SurveyRiskSuggestionsTable } from "@/components/gro/SurveyRiskSuggestionsTable";
import { PaginationFooter } from "@/components/shared/PaginationFooter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSurveyCampaignDetail } from "@/hooks/useSurveyCampaigns";
import { downloadSurveyCampaignResponsesXlsx } from "@/services/surveyResults";
import {
  useApproveSurveyCampaignAiRisk,
  useGenerateSurveyCampaignAiSuggestions,
  useRebuildSurveyCampaignAnalytics,
  useRejectSurveyCampaignAiRisk,
  useResetSurveyCampaignAiRisk,
  useSurveyCampaignAiSuggestions,
  useUndoSurveyCampaignAiRiskApproval,
  useSurveyCampaignCuts,
  useSurveyCampaignResults,
  useSurveyCampaignResponseSummary,
  useUpdateSurveyCampaignAiSuggestionFeedback,
  useSurveyRiskSuggestions,
} from "@/hooks/useSurveyResults";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { AiFeedbackStatus, AiGeneratedOutput } from "@/types/ai";
import type { SurveyResultCutType } from "@/types/gro";

type ResultsTab = "dimensions" | "cuts" | "suggestions" | "ai_history";
type CutFilterValue = SurveyResultCutType | "none" | "all";

function formatCurrency(value?: string | number | null, currency = "BRL") {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(Number.isFinite(n) ? n : 0);
}

function formatInteger(value?: string | number | null) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const day = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date);
    const time = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date);
    return `${day} às ${time}`;
  } catch {
    return value;
  }
}

function statusLabel(status?: string | null) {
  const map: Record<string, string> = {
    draft: "Rascunho gerado",
    pending_review: "Aguardando revisão",
    partially_approved: "Parcialmente aprovado",
    approved: "Aprovado",
    rejected: "Rejeitado",
    applied_to_inventory: "Aplicado ao inventário",
    sent_to_moderation: "Enviado para moderação",
    failed: "Falha na geração",
    applied: "Aplicado",
  };
  return map[status || ""] || status || "-";
}

function statusClass(status?: string | null) {
  if (status === "rejected" || status === "failed") return "border-red-200 bg-red-50 text-red-700";
  if (status === "approved" || status === "applied_to_inventory" || status === "applied") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "sent_to_moderation" || status === "partially_approved") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-blue-200 bg-blue-50 text-blue-700";
}



function riskReviewStatusLabel(status?: string | null) {
  const map: Record<string, string> = {
    pending_review: "Pendente de revisão",
    rejected: "Rejeitado",
    applied_to_inventory: "Aplicado ao inventário",
  };
  return map[status || "pending_review"] || status || "Pendente de revisão";
}

function riskReviewStatusClass(status?: string | null) {
  if (status === "rejected") return "border-red-200 bg-red-50 text-red-700";
  if (status === "applied_to_inventory") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function normalizeTextKey(value?: string | number | null) {
  return String(value ?? "").trim().toLowerCase().replace(/[_-]/g, " ").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function numberFromLevel(value?: string | number | null) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return Math.min(5, Math.max(1, Math.round(n)));
  const key = normalizeTextKey(value);
  const map: Record<string, number> = {
    "very low": 1,
    "muito baixo": 1,
    "muito baixa": 1,
    raro: 1,
    rara: 1,
    rare: 1,
    low: 2,
    baixo: 2,
    baixa: 2,
    "medium low": 2,
    moderate: 3,
    moderado: 3,
    moderada: 3,
    medium: 3,
    medio: 3,
    media: 3,
    "medium high": 4,
    high: 4,
    alto: 4,
    alta: 4,
    critical: 5,
    critico: 5,
    critica: 5,
    "very high": 5,
  };
  return map[key] || 0;
}

function translateLevelLabel(label?: string | number | null, score?: string | number | null) {
  const key = normalizeTextKey(label);
  const map: Record<string, string> = {
    "very low": "Muito baixa",
    "muito baixo": "Muito baixa",
    "muito baixa": "Muito baixa",
    rare: "Rara",
    raro: "Rara",
    rara: "Rara",
    low: "Baixa",
    baixo: "Baixa",
    baixa: "Baixa",
    "medium low": "Média-baixa",
    moderate: "Moderada",
    moderado: "Moderada",
    moderada: "Moderada",
    medium: "Moderada",
    medio: "Moderada",
    media: "Moderada",
    "medium high": "Média-alta",
    high: "Alta",
    alto: "Alta",
    alta: "Alta",
    critical: "Crítica",
    critico: "Crítica",
    critica: "Crítica",
    "very high": "Crítica",
  };
  if (map[key]) return map[key];
  const n = numberFromLevel(score);
  const byScore: Record<number, string> = { 1: "Muito baixa", 2: "Baixa", 3: "Moderada", 4: "Alta", 5: "Crítica" };
  return n ? byScore[n] : String(label || "-");
}

function formatLevel(score?: string | number | null, label?: string | number | null) {
  const n = numberFromLevel(score) || numberFromLevel(label);
  const translated = translateLevelLabel(label, score || n);
  return n ? `${n} - ${translated}` : translated || "-";
}

function riskClassificationLabel(classification?: string | null, probabilityScore?: string | number | null, severityScore?: string | number | null) {
  const key = normalizeTextKey(classification);
  const map: Record<string, string> = {
    low: "Baixo",
    baixo: "Baixo",
    moderate: "Moderado",
    moderado: "Moderado",
    medium: "Moderado",
    high: "Alto",
    alto: "Alto",
    critical: "Crítico",
    critico: "Crítico",
    organizational_process: "",
    "organizational process": "",
  };
  if (map[key]) return map[key];
  const p = numberFromLevel(probabilityScore);
  const sev = numberFromLevel(severityScore);
  if (p && sev) {
    const score = p * sev;
    if (score <= 4) return "Baixo";
    if (score <= 9) return "Moderado";
    if (score <= 16) return "Alto";
    return "Crítico";
  }
  return classification || "-";
}

function priorityLabel(priority?: string) {
  const map: Record<string, string> = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    critical: "Crítica",
  };
  return map[priority || ""] || priority || "-";
}

function countPlans(output: AiGeneratedOutput["output"]) {
  return (output.risks || []).reduce((sum, risk) => sum + (risk.action_plans?.length || 0), 0);
}

function countTasks(output: AiGeneratedOutput["output"]) {
  return (output.risks || []).reduce(
    (sum, risk) => sum + (risk.action_plans || []).reduce((planSum, plan) => planSum + (plan.tasks?.length || 0), 0),
    0,
  );
}



function FeedbackButtons({
  value,
  onChange,
  disabled,
}: {
  value?: AiFeedbackStatus;
  onChange: (value: AiFeedbackStatus) => void;
  disabled?: boolean;
}) {
  const current = value ?? null;
  const nextLike = current === "like" ? null : "like";
  const nextUnlike = current === "unlike" ? null : "unlike";

  return (
    <div className="flex items-center gap-1" aria-label="Avaliação da recomendação Normex Copilot">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-8 w-8 rounded-full ${current === "like" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700" : "text-slate-400 hover:text-emerald-600"}`}
        onClick={() => onChange(nextLike)}
        disabled={disabled}
        title="Marcar como útil"
        aria-label="Marcar como útil"
      >
        <ThumbsUp className={`h-4 w-4 ${current === "like" ? "fill-current" : ""}`} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-8 w-8 rounded-full ${current === "unlike" ? "bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700" : "text-slate-400 hover:text-red-600"}`}
        onClick={() => onChange(nextUnlike)}
        disabled={disabled}
        title="Marcar como não útil"
        aria-label="Marcar como não útil"
      >
        <ThumbsDown className={`h-4 w-4 ${current === "unlike" ? "fill-current" : ""}`} />
      </Button>
    </div>
  );
}

function AiSuggestionExecutionCard({
  item,
  expanded,
  onToggle,
  onFeedback,
  isFeedbackSaving,
  canWrite,
  onRiskAction,
  riskActionBusy,
}: {
  item: AiGeneratedOutput;
  expanded: boolean;
  onToggle: () => void;
  onFeedback: (value: AiFeedbackStatus) => void;
  isFeedbackSaving: boolean;
  canWrite: boolean;
  onRiskAction: (action: "approve" | "reject" | "reset" | "undo", outputId: string, riskIndex: number) => void;
  riskActionBusy: boolean;
}) {
  const usage = item.usage;
  const risks = item.output?.risks || [];

  return (
    <Card className="overflow-hidden border-slate-200">
      <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold">Execução Normex Copilot - {formatDateTime(item.created_at)}</div>
            <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>{statusLabel(item.status)}</span>
          </div>
          <div className="text-sm text-muted-foreground">{item.output?.summary || "Sugestões geradas e salvas no histórico da campanha."}</div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>•</span>
            <span>Riscos: {risks.length}</span>
            <span>•</span>
            <span>Planos: {countPlans(item.output)}</span>
            <span>•</span>
            <span>Ações: {countTasks(item.output)}</span>
          </div>
        </div>

        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3 lg:min-w-[420px]">
          <div className="rounded-lg border bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide">Tokens de entrada</div>
            <div className="text-sm font-semibold text-slate-900">{formatInteger(usage?.input_tokens)}</div>
          </div>
          <div className="rounded-lg border bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide">Tokens de saída</div>
            <div className="text-sm font-semibold text-slate-900">{formatInteger(usage?.output_tokens)}</div>
          </div>
          <div className="rounded-lg border bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide">Valor debitado</div>
            <div className="text-sm font-semibold text-slate-900">{formatCurrency(usage?.charged_amount_brl)}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
        <div className="text-xs text-muted-foreground">
          Consultar este histórico não consome créditos. Só uma nova execução chama o Normex Copilot novamente.
        </div>
        <div className="flex items-center gap-2">
          <FeedbackButtons value={item.feedback_status ?? null} onChange={onFeedback} disabled={isFeedbackSaving} />
          <Button variant="outline" size="sm" onClick={onToggle}>
            {expanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
            Ver detalhes
          </Button>
        </div>
      </div>

      {expanded ? (
        <div className="space-y-4 border-t p-4">
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
            <div className="font-medium">Revisão das sugestões Normex Copilot</div>
            <div className="mt-1">
              Cada risco possui revisão própria. Ao aprovar, o risco, plano de ação e ações sugeridas são aplicados diretamente ao inventário vinculado à campanha. A rejeição permanece no histórico e pode ser cancelada depois.
            </div>
          </div>

          {risks.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground">O Normex Copilot não encontrou lacunas relevantes para novos riscos com base no contexto enviado.</Card>
          ) : (
            <div className="space-y-5">
              {risks.map((risk, index) => (
                <Card key={`${risk.title}-${index}`} className="overflow-hidden border-slate-200">
                  <div className="border-b bg-slate-50 px-4 py-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">1. Risco sugerido</div>
                    <div className="mt-1 text-base font-semibold text-slate-950">{risk.title || "Risco sem título"}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{risk.description || risk.technical_description || "Sem descrição informada pelo Normex Copilot."}</div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full border bg-white px-2 py-1">Classificação: {riskClassificationLabel(risk.classification, risk.probability_score, risk.severity_score)}</span>
                      <span className="rounded-full border bg-white px-2 py-1">Probabilidade: {formatLevel(risk.probability_score, risk.probability_label)}</span>
                      <span className="rounded-full border bg-white px-2 py-1">Severidade: {formatLevel(risk.severity_score, risk.severity_label)}</span>
                      <span className="rounded-full border bg-white px-2 py-1">Confiança: {Math.round(Number(risk.confidence || 0) * 100)}%</span>
                      <span className={`rounded-full border px-2 py-1 ${riskReviewStatusClass(risk.review_status)}`}>Status: {riskReviewStatusLabel(risk.review_status)}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(!risk.review_status || risk.review_status === "pending_review") && canWrite ? (
                        <>
                          <Button size="sm" onClick={() => onRiskAction("approve", item.id, index)} disabled={riskActionBusy}>Aprovar risco</Button>
                          <Button size="sm" variant="outline" onClick={() => onRiskAction("reject", item.id, index)} disabled={riskActionBusy}>Rejeitar risco</Button>
                        </>
                      ) : null}
                      {risk.review_status === "rejected" && canWrite ? (
                        <Button size="sm" variant="outline" onClick={() => onRiskAction("reset", item.id, index)} disabled={riskActionBusy}>Cancelar rejeição</Button>
                      ) : null}
                      {risk.review_status === "applied_to_inventory" && canWrite ? (
                        <Button size="sm" variant="outline" onClick={() => onRiskAction("undo", item.id, index)} disabled={riskActionBusy}>Desfazer aprovação</Button>
                      ) : null}
                    </div>
                    {risk.review_status === "applied_to_inventory" ? (
                      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                        Este risco foi aplicado ao inventário vinculado à campanha.
                      </div>
                    ) : null}
                    {risk.review_status === "rejected" ? (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                        Este risco foi rejeitado e permanece registrado no histórico do Normex Copilot.
                      </div>
                    ) : null}
                  </div>

                  <div className="grid gap-3 p-4 lg:grid-cols-2">
                    <div className="rounded-xl border bg-white p-3 text-sm">
                      <div className="font-medium text-slate-900">Evidências usadas pelo Normex Copilot</div>
                      {(risk.evidences || []).length ? (
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                          {risk.evidences?.map((evidence, i) => <li key={i}>{evidence}</li>)}
                        </ul>
                      ) : (
                        <p className="mt-2 text-muted-foreground">Nenhuma evidência detalhada informada.</p>
                      )}
                    </div>
                    <div className="rounded-xl border bg-white p-3 text-sm">
                      <div className="font-medium text-slate-900">Por que não parece duplicado</div>
                      <p className="mt-2 text-muted-foreground">{risk.why_not_duplicate || risk.justification || "Sem justificativa detalhada informada."}</p>
                    </div>
                  </div>

                  <div className="space-y-4 border-t bg-white p-4">
                    {(risk.action_plans || []).length ? (
                      risk.action_plans?.map((plan, planIndex) => (
                        <div key={`${plan.title}-${planIndex}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">2. Plano de ação sugerido</div>
                          <div className="mt-1 text-base font-semibold text-slate-950">{plan.title || "Plano sem título"}</div>
                          <p className="mt-1 text-sm text-muted-foreground">{plan.objective || plan.description || "Sem objetivo informado pelo Normex Copilot."}</p>
                          <div className="mt-3 flex flex-wrap gap-2 text-xs">
                            <span className="rounded-full border bg-white px-2 py-1">Prioridade: {priorityLabel(plan.priority)}</span>
                            <span className="rounded-full border bg-white px-2 py-1">Prazo sugerido: {plan.suggested_deadline_days ? `${plan.suggested_deadline_days} dias` : "-"}</span>
                            <span className="rounded-full border bg-white px-2 py-1">Responsável: {plan.responsible_area || "-"}</span>
                          </div>

                          <div className="mt-4 rounded-xl border bg-white p-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">3. Ações deste plano sugeridas</div>
                            {(plan.tasks || []).length ? (
                              <div className="mt-3 space-y-3">
                                {plan.tasks?.map((task, taskIndex) => (
                                  <div key={`${task.title}-${taskIndex}`} className="rounded-lg border bg-slate-50 p-3">
                                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                                      <div>
                                        <div className="font-medium text-slate-950">{taskIndex + 1}. {task.title || "Ação sem título"}</div>
                                        <p className="mt-1 text-sm text-muted-foreground">{task.description || "Sem descrição informada."}</p>
                                      </div>
                                      <span className="w-fit rounded-full border bg-white px-2 py-1 text-xs">{priorityLabel(task.priority)}</span>
                                    </div>
                                    <div className="mt-2 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                                      <div><span className="font-medium text-slate-700">Prazo:</span> {task.suggested_deadline_days ? `${task.suggested_deadline_days} dias` : "-"}</div>
                                      <div><span className="font-medium text-slate-700">Responsável:</span> {task.responsible_area || "-"}</div>
                                      <div><span className="font-medium text-slate-700">Evidência:</span> {task.evidence_required || "-"}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-2 text-sm text-muted-foreground">Nenhuma ação sugerida para este plano.</p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <Card className="p-4 text-sm text-muted-foreground">Nenhum plano de ação sugerido para este risco.</Card>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {(item.output?.general_recommendations || []).length ? (
            <Card className="p-4 text-sm">
              <div className="font-medium">Recomendações gerais</div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {item.output.general_recommendations?.map((value, index) => <li key={index}>{value}</li>)}
              </ul>
            </Card>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export default function SurveyResultsPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { user } = useAuth();

  const canRead = user?.permissions?.includes(PERMISSIONS.SURVEY_RESULTS_READ) ?? false;
  const canWrite = user?.permissions?.includes(PERMISSIONS.RISK_INVENTORY_WRITE) ?? false;
  const [activeTab, setActiveTab] = useState<ResultsTab>("dimensions");
  const [cutType, setCutType] = useState<CutFilterValue>("none");
  const [riskLevelFilter, setRiskLevelFilter] = useState<"all" | "low" | "moderate" | "high" | "critical">("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<10 | 20 | 30>(10);
  const [aiPage, setAiPage] = useState(1);
  const [confirmRebuildOpen, setConfirmRebuildOpen] = useState(false);
  const [confirmAiSuggestionsOpen, setConfirmAiSuggestionsOpen] = useState(false);
  const [expandedAiOutputId, setExpandedAiOutputId] = useState<string | null>(null);
  const [lastGeneratedAiSuggestion, setLastGeneratedAiSuggestion] = useState<AiGeneratedOutput | null>(null);
  const [riskActionConfirm, setRiskActionConfirm] = useState<{ action: "approve" | "reject" | "reset" | "undo"; outputId: string; riskIndex: number } | null>(null);
  const [isDownloadingResponses, setIsDownloadingResponses] = useState(false);

  const shouldLoadCuts = cutType !== "none";
  const campaignQ = useSurveyCampaignDetail(campaignId);
  const summaryQ = useSurveyCampaignResponseSummary(campaignId);
  const resultsQ = useSurveyCampaignResults(campaignId);
  const cutsQ = useSurveyCampaignCuts(shouldLoadCuts ? campaignId : undefined, {
    cut_type: shouldLoadCuts && cutType !== "all" ? cutType : undefined,
  });
  const suggestionsQ = useSurveyRiskSuggestions(campaignId, {
    cut_type: undefined,
    risk_level_code: riskLevelFilter === "all" ? undefined : riskLevelFilter,
    page,
    per_page: perPage,
  });
  const aiHistoryQ = useSurveyCampaignAiSuggestions(campaignId, { page: aiPage, per_page: 10 });
  const rebuildMut = useRebuildSurveyCampaignAnalytics();
  const aiSuggestionsMut = useGenerateSurveyCampaignAiSuggestions();
  const feedbackAiMut = useUpdateSurveyCampaignAiSuggestionFeedback();
  const approveAiRiskMut = useApproveSurveyCampaignAiRisk();
  const rejectAiRiskMut = useRejectSurveyCampaignAiRisk();
  const resetAiRiskMut = useResetSurveyCampaignAiRisk();
  const undoAiRiskMut = useUndoSurveyCampaignAiRiskApproval();

  const campaign = campaignQ.data;
  const canGenerateAiSuggestions = campaign?.status === "closed";
  const isClosedCampaign = campaign?.status === "closed" || campaign?.status === "cancelled";
  const aiHistoryBase = aiHistoryQ.data ?? { items: [], total: 0, page: 1, per_page: 10, total_pages: 1 };
  const aiHistory = useMemo(() => {
    if (!lastGeneratedAiSuggestion) return aiHistoryBase;
    const alreadyListed = aiHistoryBase.items.some((item) => item.id === lastGeneratedAiSuggestion.id);
    if (alreadyListed) return aiHistoryBase;
    return {
      ...aiHistoryBase,
      items: [lastGeneratedAiSuggestion, ...aiHistoryBase.items],
      total: aiHistoryBase.total + 1,
      total_pages: Math.max(aiHistoryBase.total_pages, 1),
    };
  }, [aiHistoryBase, lastGeneratedAiSuggestion]);
  const hasPreviousAiExecutions = aiHistory.total > 0;
  const aiRiskActionBusy = approveAiRiskMut.isPending || rejectAiRiskMut.isPending || resetAiRiskMut.isPending || undoAiRiskMut.isPending;
  const aiConfirmDescription = useMemo(() => {
    if (hasPreviousAiExecutions) {
      return 'Esta campanha já possui uma ou mais execuções do Normex Copilot. Você pode consultar o histórico na aba "Histórico Normex Copilot" sem consumir novos créditos. Deseja mesmo gerar uma nova execução com Normex Copilot? Esta ação consumirá novos créditos.';
    }
    return "O Normex Copilot analisará a campanha encerrada, riscos já cadastrados, sugestões existentes e modelos de planos para sugerir apenas novos riscos, planos e ações. O uso será debitado dos créditos Normex Copilot do tenant.";
  }, [hasPreviousAiExecutions]);

  async function handleDownloadResponses() {
    if (!campaignId) return;

    try {
      setIsDownloadingResponses(true);
      const { blob, filename } = await downloadSurveyCampaignResponsesXlsx(campaignId);
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = filename || `respostas-questionario-${campaignId}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Download da planilha iniciado.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao baixar a planilha de respostas.");
    } finally {
      setIsDownloadingResponses(false);
    }
  }

  async function handleConfirmAiSuggestions() {
    if (!campaignId) return;
    try {
      const result = await aiSuggestionsMut.mutateAsync({ campaignId });
      const risksCount = result.output?.risks?.length ?? 0;
      const generatedOutput: AiGeneratedOutput = {
        id: result.id,
        tenant_id: "",
        user_id: null,
        usage_log_id: result.usage_log_id,
        feature: result.usage?.feature || "campaign_risk_action_suggestions",
        entity_type: "survey_campaign",
        entity_id: campaignId,
        provider: result.usage?.provider || "openai",
        model: result.usage?.model || "",
        prompt_version: result.prompt_version,
        input_context: null,
        output: result.output,
        status: result.status,
        feedback_status: null,
        feedback_by: null,
        feedback_at: null,
        reviewed_by: null,
        reviewed_at: null,
        approved_at: null,
        rejected_at: null,
        created_at: result.created_at,
        usage: result.usage,
      };
      setLastGeneratedAiSuggestion(generatedOutput);
      setAiPage(1);
      setExpandedAiOutputId(result.id);
      setActiveTab("ai_history");
      await aiHistoryQ.refetch();
      toast.success(`Sugestões com Normex Copilot geradas e salvas no histórico. Riscos sugeridos: ${risksCount}.`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === "insufficient_ai_credits") {
        toast.error("Saldo de créditos Normex Copilot insuficiente para gerar sugestões.");
      } else if (detail === "ai_disabled") {
        toast.error("Normex Copilot está desabilitado na plataforma.");
      } else {
        toast.error(detail || "Erro ao gerar sugestões com Normex Copilot.");
      }
    } finally {
      setConfirmAiSuggestionsOpen(false);
    }
  }


  const riskActionDialog = useMemo(() => {
    if (!riskActionConfirm) return null;
    const content = {
      approve: {
        title: "Aprovar risco sugerido pelo Normex Copilot?",
        description: "Ao confirmar, este risco será criado diretamente no inventário vinculado à campanha, junto com o plano de ação e as ações sugeridas. Esta ação não cria registros na base cadastral de riscos ou planos.",
        confirmLabel: "Aprovar e criar no inventário",
        variant: "default" as const,
      },
      reject: {
        title: "Rejeitar risco sugerido?",
        description: "Este risco permanecerá no histórico do Normex Copilot com status Rejeitado. Você poderá cancelar a rejeição posteriormente.",
        confirmLabel: "Rejeitar risco",
        variant: "destructive" as const,
      },
      reset: {
        title: "Cancelar rejeição?",
        description: "Este risco voltará para Pendente de revisão e poderá ser aprovado posteriormente.",
        confirmLabel: "Cancelar rejeição",
        variant: "default" as const,
      },
      undo: {
        title: "Desfazer aprovação?",
        description: "Esta ação tentará remover do inventário o risco, o plano de ação e as ações criadas a partir desta sugestão do Normex Copilot. Se houver plano ou ação em execução, a operação será bloqueada.",
        confirmLabel: "Desfazer aprovação",
        variant: "destructive" as const,
      },
    };
    return content[riskActionConfirm.action];
  }, [riskActionConfirm]);

  function handleAiRiskAction(action: "approve" | "reject" | "reset" | "undo", outputId: string, riskIndex: number) {
    if (!campaignId) return;
    setRiskActionConfirm({ action, outputId, riskIndex });
  }

  async function handleConfirmAiRiskAction() {
    if (!campaignId || !riskActionConfirm) return;
    const { action, outputId, riskIndex } = riskActionConfirm;
    try {
      if (action === "approve") await approveAiRiskMut.mutateAsync({ campaignId, outputId, riskIndex });
      if (action === "reject") await rejectAiRiskMut.mutateAsync({ campaignId, outputId, riskIndex });
      if (action === "reset") await resetAiRiskMut.mutateAsync({ campaignId, outputId, riskIndex });
      if (action === "undo") await undoAiRiskMut.mutateAsync({ campaignId, outputId, riskIndex });
      await aiHistoryQ.refetch();
      setLastGeneratedAiSuggestion(null);
      toast.success("Status do risco Normex Copilot atualizado com sucesso.");
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message;
      toast.error(detail || "Erro ao atualizar o risco sugerido pelo Normex Copilot.");
    } finally {
      setRiskActionConfirm(null);
    }
  }

  async function handleAiFeedback(outputId: string, feedbackStatus: AiFeedbackStatus) {
    if (!campaignId) return;
    try {
      await feedbackAiMut.mutateAsync({ campaignId, outputId, feedbackStatus });
      await aiHistoryQ.refetch();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao registrar avaliação da recomendação Normex Copilot.");
    }
  }

  async function handleConfirmRebuild() {
    if (!campaignId) return;
    try {
      const result = await rebuildMut.mutateAsync({
        campaignId,
        payload: { confirm_reset_decisions: true },
      });
      toast.success(`Analytics recalculado. Cortes: ${result.cuts_created}. Sugestões: ${result.suggestions_created}.`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao recalcular analytics.");
    } finally {
      setConfirmRebuildOpen(false);
    }
  }

  const pageLoading = campaignQ.isLoading || summaryQ.isLoading || resultsQ.isLoading;
  const summary = summaryQ.data || resultsQ.data?.summary;
  const dimensions = resultsQ.data?.dimensions ?? [];
  const cuts = cutsQ.data ?? [];
  const suggestionPage = suggestionsQ.data ?? { items: [], total: 0, page: 1, per_page: perPage, total_pages: 1 };

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(campaignId ? `/gro/campaigns/${campaignId}` : "/gro/campaigns")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{campaign?.name || "Resultados Psicossociais"}</h1>
              <p className="text-sm text-muted-foreground">Acompanhe respostas, dimensões, cortes analíticos, previsão de riscos e histórico do Normex Copilot.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-end md:self-auto">
            {campaign && ["published", "closed"].includes(campaign.status) ? (
              <Button variant="outline" onClick={handleDownloadResponses} disabled={isDownloadingResponses}>
                <Download className={`mr-2 h-4 w-4 ${isDownloadingResponses ? "animate-pulse" : ""}`} />
                Baixar respostas (.xlsx)
              </Button>
            ) : null}

            {canGenerateAiSuggestions ? (
              <Button variant="outline" onClick={() => setConfirmAiSuggestionsOpen(true)} disabled={aiSuggestionsMut.isPending}>
                <Sparkles className={`mr-2 h-4 w-4 ${aiSuggestionsMut.isPending ? "animate-pulse" : ""}`} />
                Gerar sugestões com Normex Copilot
              </Button>
            ) : null}

            {!isClosedCampaign ? (
              <Button onClick={() => setConfirmRebuildOpen(true)} disabled={rebuildMut.isPending}>
                <RefreshCw className={`mr-2 h-4 w-4 ${rebuildMut.isPending ? "animate-spin" : ""}`} />
                Recalcular analytics
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">Você não possui permissão para visualizar resultados.</Card>
      ) : pageLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando resultados...</Card>
      ) : campaignQ.isError || !summary ? (
        <Card className="p-6"><div className="text-sm text-destructive">{(campaignQ.error as any)?.message || "Erro ao carregar resultados"}</div></Card>
      ) : (
        <div className="space-y-6">
          <SurveyResultsSummary summary={summary} dimensions={dimensions} />
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ResultsTab)} className="space-y-4">
            <TabsList>
              <TabsTrigger value="dimensions">Dimensões</TabsTrigger>
              <TabsTrigger value="cuts">Cortes analíticos</TabsTrigger>
              <TabsTrigger value="suggestions">Previsão de riscos</TabsTrigger>
              <TabsTrigger value="ai_history">Histórico Normex Copilot</TabsTrigger>
            </TabsList>

            <TabsContent value="dimensions"><SurveyDimensionsSection dimensions={dimensions} /></TabsContent>

            <TabsContent value="cuts">
              <Card className="p-4">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="font-semibold">Cortes analíticos</div>
                  <Select value={cutType} onValueChange={(value) => setCutType(value as CutFilterValue)}>
                    <SelectTrigger className="w-full lg:w-[220px]"><SelectValue placeholder="Selecione um corte" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione um corte</SelectItem>
                      <SelectItem value="unit">Unidade</SelectItem>
                      <SelectItem value="department">Departamento</SelectItem>
                      <SelectItem value="job_position">Cargo</SelectItem>
                      <SelectItem value="worker_group">Grupo</SelectItem>
                      <SelectItem value="all">Todos os cortes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cutType === "none" ? (
                  <Card className="p-6 text-sm text-muted-foreground">Selecione um corte para carregar os resultados analíticos.</Card>
                ) : cutsQ.isLoading ? (
                  <Card className="p-6 text-sm text-muted-foreground">Carregando cortes analíticos...</Card>
                ) : cutsQ.isError ? (
                  <Card className="p-6 text-sm text-destructive">{(cutsQ.error as any)?.message || "Erro ao carregar cortes analíticos."}</Card>
                ) : (
                  <SurveyCutsTable items={cuts} />
                )}
              </Card>
            </TabsContent>

            <TabsContent value="suggestions">
              <Card className="p-4">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1">
                    <div className="font-semibold">Previsão de riscos</div>
                    <div className="text-sm text-muted-foreground">Enquanto a campanha estiver em andamento, esta tela serve apenas para acompanhamento. A decisão fica na Fila de Moderação.</div>
                  </div>

                  <div className="flex items-center gap-2 lg:justify-end">
                    <span className="text-sm text-muted-foreground">Classificação</span>
                    <Select value={riskLevelFilter} onValueChange={(value) => { setRiskLevelFilter(value as any); setPage(1); }}>
                      <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="low">Baixo</SelectItem>
                        <SelectItem value="moderate">Moderado</SelectItem>
                        <SelectItem value="high">Alto</SelectItem>
                        <SelectItem value="critical">Crítico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {suggestionsQ.isLoading ? (
                  <Card className="p-6 text-sm text-muted-foreground">Carregando previsão de riscos...</Card>
                ) : suggestionsQ.isError ? (
                  <Card className="p-6 text-sm text-destructive">{(suggestionsQ.error as any)?.message || "Erro ao carregar a previsão de riscos."}</Card>
                ) : (
                  <>
                    <SurveyRiskSuggestionsTable items={suggestionPage.items} />
                    <div className="mt-4">
                      <PaginationFooter
                        page={suggestionPage.page}
                        perPage={suggestionPage.per_page as 10 | 20 | 30}
                        total={suggestionPage.total}
                        totalPages={suggestionPage.total_pages}
                        onPageChange={setPage}
                        onPerPageChange={(value) => { setPerPage(value); setPage(1); }}
                        label="resultados"
                      />
                    </div>
                  </>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="ai_history">
              <Card className="p-4">
                <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="font-semibold">Histórico Normex Copilot</div>
                    <div className="text-sm text-muted-foreground">Consulte execuções já geradas pelo Normex Copilot sem consumir novos créditos. Para gerar novamente, use o botão “Gerar sugestões com Normex Copilot”.</div>
                  </div>
                  <div className="rounded-lg border bg-slate-50 px-3 py-2 text-xs text-muted-foreground">
                    Execuções: <strong className="text-slate-900">{aiHistory.total}</strong>
                  </div>
                </div>

                {aiSuggestionsMut.isPending ? (
                  <Card className="mb-4 border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" aria-hidden="true" />
                      <div>
                        <div className="font-medium">Gerando sugestões com Normex Copilot...</div>
                        <div className="mt-1 text-blue-800">Coletando resultados consolidados, comparando riscos/planos existentes e salvando histórico da execução.</div>
                      </div>
                    </div>
                  </Card>
                ) : null}

                {aiHistoryQ.isLoading ? (
                  <Card className="p-6 text-sm text-muted-foreground">Carregando histórico do Normex Copilot...</Card>
                ) : aiHistoryQ.isError ? (
                  <Card className="p-6 text-sm text-destructive">{(aiHistoryQ.error as any)?.message || "Erro ao carregar histórico do Normex Copilot."}</Card>
                ) : aiHistory.items.length === 0 ? (
                  <Card className="p-6 text-sm text-muted-foreground">Ainda não há execuções do Normex Copilot para esta campanha.</Card>
                ) : (
                  <div className="space-y-4">
                    {aiHistory.items.map((item) => (
                      <AiSuggestionExecutionCard
                        key={item.id}
                        item={item}
                        expanded={expandedAiOutputId === item.id}
                        onToggle={() => setExpandedAiOutputId(expandedAiOutputId === item.id ? null : item.id)}
                        onFeedback={(value) => handleAiFeedback(item.id, value)}
                        isFeedbackSaving={feedbackAiMut.isPending}
                        canWrite={canWrite}
                        onRiskAction={handleAiRiskAction}
                        riskActionBusy={aiRiskActionBusy}
                      />
                    ))}
                    <PaginationFooter
                      page={aiHistory.page}
                      perPage={aiHistory.per_page as 10 | 20 | 30}
                      total={aiHistory.total}
                      totalPages={aiHistory.total_pages}
                      onPageChange={setAiPage}
                      onPerPageChange={() => setAiPage(1)}
                      label="execuções"
                    />
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      <ConfirmDialog
        open={confirmAiSuggestionsOpen}
        onOpenChange={setConfirmAiSuggestionsOpen}
        title={hasPreviousAiExecutions ? "Gerar nova execução com Normex Copilot?" : "Gerar sugestões com Normex Copilot?"}
        description={aiConfirmDescription}
        onConfirm={handleConfirmAiSuggestions}
        confirmLabel={hasPreviousAiExecutions ? "Gerar nova execução" : "Gerar com Normex Copilot"}
      />

      {riskActionDialog ? (
        <ConfirmDialog
          open={!!riskActionConfirm}
          onOpenChange={(open) => { if (!open) setRiskActionConfirm(null); }}
          title={riskActionDialog.title}
          description={riskActionDialog.description}
          onConfirm={handleConfirmAiRiskAction}
          confirmLabel={riskActionDialog.confirmLabel}
          variant={riskActionDialog.variant}
        />
      ) : null}

      <ConfirmDialog
        open={confirmRebuildOpen}
        onOpenChange={setConfirmRebuildOpen}
        title="Recalcular analytics?"
        description="Essa ação recalcula a previsão atual dos riscos com base nas respostas recebidas até agora. Deseja continuar?"
        onConfirm={handleConfirmRebuild}
        confirmLabel="Recalcular"
      />
    </div>
  );
}
